/**
 * Expanded detail for a VSC report.
 */
import * as React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FileText, Calendar, Building2, UserCheck, Star, CheckCircle2 } from 'lucide-react';
import { DetailBadge } from '@/components/common/details/DetailBadge';
import { DetailField } from '@/components/common/details/DetailField';
import { DetailsCard } from '@/components/common/DetailsCard';
import { Button } from '@/components/forms/Button';
import { ValidateModal } from '@/components/common/modals/ValidateModal';
import { usePageActions } from '@/hooks/usePageActions';
import { useConnectedUser } from '@/hooks/useConnectedUser';
import { cn } from '@/lib/utils';
import { getRoleLabel } from '@/utils/role-label-utils';
import { updateVscReport, validateVscReport } from '@/services/vsc-reports.service';
import { VSC_RATING_VALUES } from '@/components/vsc-reports/vsc-report-configs';
import {
  deriveVscDetailDisplay,
  formatVscDateTime,
  getVscReportDetailCapabilities,
  getVscUpdateSuccessMessage,
} from '@/components/vsc-reports/vsc-report-table-utils';
import { buildSubmitDto, toDetailDraft } from '@/components/vsc-reports/vsc-report-form-utils';
import { toast } from '@/components/ui/toast';
import { getErrorMessage } from '@/utils/errors-utils';
import type { VscReport } from '@/types/vsc-report.types';

/**
 * Detail / edit body for one VSC report (table collapse or {@link EditVscReportView}).
 * Rating + comment draft; submit sets `submitted`; validate is a separate confirm.
 */
export const VscReportDetailsPanel: React.FC<{
  /** VSC report shown in the collapse. */
  vscReport: VscReport;
  /** `view` locks the form; `edit` is the table-collapse default. */
  presentationMode?: 'view' | 'edit';
  /** Hide the title/badge header when the parent already renders one. */
  hideDetailHeader?: boolean;
  /** Called after submit/validate so the parent can collapse or refetch. */
  onActionSuccess?: () => void;
}> = ({ vscReport, presentationMode = 'edit', hideDetailHeader = false, onActionSuccess }) => {
  const queryClient = useQueryClient();
  const { canValidate } = usePageActions('vscReports');
  const { user, isVolunteer, isVsc, isAdmin, isManager, isCoordinator } = useConnectedUser();
  const [draft, setDraft] = React.useState(toDetailDraft(vscReport));
  const [validateOpen, setValidateOpen] = React.useState(false);

  /** Re-hydrate the draft when a different report is expanded or the server payload changes. */
  React.useEffect(() => {
    setDraft(toDetailDraft(vscReport));
  }, [vscReport.id, vscReport.rating, vscReport.comment]);

  const updateMutation = useMutation({
    mutationFn: (dto: Parameters<typeof updateVscReport>[1]) => updateVscReport(vscReport.id, dto),
    onSuccess: async (_saved, dto) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['vsc-reports'], refetchType: 'all' }),
        queryClient.invalidateQueries({ queryKey: ['vsc-report'], refetchType: 'all' }),
      ]);
      toast.success(getVscUpdateSuccessMessage(dto.status));
      if (dto.status === 'submitted') {
        onActionSuccess?.();
      }
    },
    onError: (e: unknown) => {
      toast.error(getErrorMessage(e));
    },
  });

  const validateMutation = useMutation({
    mutationFn: () => validateVscReport(vscReport.id),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['vsc-reports'], refetchType: 'all' }),
        queryClient.invalidateQueries({ queryKey: ['vsc-report'], refetchType: 'all' }),
      ]);
      toast.success('Rapport validé');
      setValidateOpen(false);
      onActionSuccess?.();
    },
    onError: (e: unknown) => {
      toast.error(getErrorMessage(e));
    },
  });

  const {
    statusConfig,
    StatusIcon,
    volunteerName,
    headerSubtitle,
    establishmentName,
    reportDateLabel,
    sessionTimeRange,
    statusColor,
  } = deriveVscDetailDisplay(vscReport);

  const { formDisabled, canSubmit, canValidateUi } = getVscReportDetailCapabilities({
    vscReport,
    presentationMode,
    userId: user?.id,
    isVolunteer,
    isVsc,
    isAdmin,
    isManager,
    isCoordinator,
    canValidate,
    draftRating: draft.rating,
  });

  function handleSubmitReport(): void {
    updateMutation.mutate(buildSubmitDto(draft));
  }

  return (
    <>
      {!hideDetailHeader ? (
        <div className="details-view-header">
          <div className="details-view-header-left">
            <div className="details-view-header-icon">
              <FileText className="w-7 h-7 text-[var(--color-2-nuance-1)]" />
            </div>
            <div className="details-view-header-content">
              <div className="details-view-header-with-badges">
                <h3 className="text-2xl font-bold text-[var(--color-1-nuance-1)] capitalize">
                  Rapport VSC
                </h3>
                <DetailBadge
                  label={statusConfig.label}
                  icon={StatusIcon}
                  colorName={statusColor}
                  variant="filled"
                />
              </div>
              <p className="text-base text-[var(--color-1-nuance-2)]">{headerSubtitle}</p>
            </div>
          </div>
        </div>
      ) : null}

      <div className="details-view-grid">
        <DetailsCard icon={Calendar} title="Séance de référence" colorName="color-2" colSpan={1}>
          <div className="space-y-2">
            <DetailField label="Date du rapport" value={reportDateLabel} />
            {sessionTimeRange && <DetailField label="Horaire" value={sessionTimeRange} />}
          </div>
        </DetailsCard>

        <DetailsCard icon={Building2} title="Établissement" colorName="color-1" colSpan={1}>
          <DetailField label="Nom" value={establishmentName} vertical />
        </DetailsCard>

        <DetailsCard
          icon={UserCheck}
          title={getRoleLabel('volunteer')}
          colorName="color-3"
          colSpan={1}
        >
          <DetailField label="Volontaire" value={volunteerName} vertical />
        </DetailsCard>

        {vscReport.status === 'validated' && vscReport.validatedAt && (
          <DetailsCard icon={CheckCircle2} title="Validation" colorName="color-3" colSpan="full">
            <DetailField label="Validé le" value={formatVscDateTime(vscReport.validatedAt)} />
          </DetailsCard>
        )}

        <DetailsCard icon={Star} title="Appréciation" colorName="color-2" colSpan="full">
          <div className={cn('space-y-4', formDisabled && 'opacity-60')}>
            <span className="text-xs font-medium text-muted-foreground">Note (1 à 5)</span>
            <div className="flex gap-1">
              {VSC_RATING_VALUES.map((n) => (
                <button
                  key={n}
                  type="button"
                  disabled={formDisabled}
                  onClick={() => setDraft((p) => ({ ...p, rating: n }))}
                  className={cn(
                    'rounded-lg p-1 transition-colors',
                    !formDisabled && 'hover:bg-[var(--color-hover)]',
                    draft.rating != null && n <= draft.rating
                      ? 'text-amber-500'
                      : 'text-muted-foreground',
                  )}
                  aria-label={`${n} étoile${n > 1 ? 's' : ''}`}
                >
                  <Star className={cn('h-7 w-7', n <= (draft.rating ?? 0) && 'fill-current')} />
                </button>
              ))}
            </div>

            <div>
              <span className="mb-2 block text-xs font-medium text-muted-foreground">
                Commentaire
              </span>
              <textarea
                disabled={formDisabled}
                value={draft.comment}
                onChange={(e) => setDraft((p) => ({ ...p, comment: e.target.value }))}
                rows={4}
                className="w-full rounded-xl border border-[color-mix(in_srgb,var(--color-1-nuance-3)_35%,transparent)] bg-[var(--card)] px-3 py-2 text-sm"
                placeholder="Votre retour sur la journée…"
              />
            </div>

            {presentationMode !== 'view' ? (
              <div className="flex flex-wrap gap-2">
                <Button
                  preset="modal-confirm"
                  disabled={!canSubmit || updateMutation.isPending}
                  onClick={handleSubmitReport}
                >
                  Soumettre
                </Button>
                {canValidateUi && (
                  <Button
                    preset="modal-confirm"
                    icon={CheckCircle2}
                    disabled={validateMutation.isPending}
                    loading={validateMutation.isPending}
                    onClick={() => setValidateOpen(true)}
                  >
                    Valider
                  </Button>
                )}
              </div>
            ) : null}
          </div>
        </DetailsCard>
      </div>

      <ValidateModal
        open={validateOpen}
        onClose={() => setValidateOpen(false)}
        onConfirm={() => void validateMutation.mutateAsync()}
        itemName="ce rapport VSC"
        isLoading={validateMutation.isPending}
        description="Confirmer la validation ? Seuls les rôles autorisés côté serveur peuvent valider."
      />
    </>
  );
};
